/**
 * Password Validation Utilities
 * 
 * Provides password validation functions matching iOS and Android implementations.
 * 
 * Validation rules:
 * - Sign-Up: 12+ characters (length only, matching iOS and Android)
 * - Sign-In: non-empty (matching Android's isNotBlank())
 * 
 * @module utils/passwordValidation
 */

/**
 * Validate password for sign-up (account creation)
 * 
 * Requirements:
 * - Minimum 12 characters
 * 
 * Matches iOS (minPasswordLength = 12) and Android (password.length >= 12).
 * 
 * @param password - Password to validate
 * @returns true if password meets sign-up requirements
 */
export function isValidSignUpPassword(password: string): boolean {
  return password.length >= 12;
}

/**
 * Validate password for sign-in
 * 
 * Requirements:
 * - Non-empty
 * 
 * Matches Android's isNotBlank(). Firebase/GCIP handles credential rejection on the backend.
 * 
 * @param password - Password to validate
 * @returns true if password is non-empty
 */
export function isValidSignInPassword(password: string): boolean {
  return password.length > 0;
}

/**
 * Get validation error message for sign-up password
 * 
 * @param password - Password to validate
 * @returns Error message or empty string if valid
 */
export function getSignUpPasswordError(password: string): string {
  if (!password) {
    return 'Password is required.';
  }
  if (password.length < 12) {
    return 'Password must be at least 12 characters.';
  }
  return '';
}

/**
 * Get validation error message for sign-in password
 * 
 * @param password - Password to validate
 * @returns Error message or empty string if valid
 */
export function getSignInPasswordError(password: string): string {
  if (!password) {
    return 'Password is required.';
  }
  return '';
}

/**
 * Get password strength description for UI display
 * 
 * @param password - Password to evaluate
 * @returns Strength description
 */
export function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  if (!password || password.length < 8) {
    return 'weak';
  }
  if (password.length >= 12) {
    return 'strong';
  }
  return 'medium';
}
